
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';

import { AuthService } from './auth.service';
import { BaseService } from './base.service';
import { LanguageService } from './language.service';
import { UtilityService } from './utility.service';

import { ENVIRONMENT } from '../../environments/environment';
import CONFIGURATION from '../../configurations/configuration';

import { EventEmitter } from '../models/generic.class';

interface IGetBorrowerRepaymentsParams {
fromDate?: Date, toDate?: Date, loanId?: number, size?: number, page?: number
};

@Injectable()
export class FinanceService {
  balanceEventEmitter: EventEmitter;
  paymentProperties: any;
  memberBalance: number;
  supportedLanguages: Array<string>;
  featureFlagObservable: Observable<import("launchdarkly-js-sdk-common").LDFlagSet>;
  ekycEsignServiceUrl: string = ENVIRONMENT.service.ees;

  constructor(
    private authService: AuthService,
    private baseService: BaseService,
    private http: HttpClient,
    private languageService: LanguageService,
    private utilityService: UtilityService,
  ) {
    this.balanceEventEmitter = new EventEmitter();
    this.supportedLanguages = CONFIGURATION.supportedLanguages;
  }

  setPaymentProperties(value: any) {
    this.paymentProperties = value;
  }

  getPaymentProperties() {
    return this.paymentProperties;
  }

  generateParams(paginate: boolean, page: number, loanTypeId: number, loanStatus: string,
    loanSubStatus: string, paymentStatus: string, startDateFrom: any, startDateTo: any,
    maturityDateFrom: any, maturityDateTo: any, recordsPerPage: number): string {
    let params: string;

    if (paginate !== null) {
      params = (params === undefined || params === '') ? '?paginate=' + paginate : params + '&paginate=' + paginate;
    }

    if (page !== 0) {
      params = (params === undefined || params === '') ? '?page=' + page : params + '&page=' + page;
    }

    if (loanTypeId !== 0) {
      params = (params === undefined || params === '') ? '?loan_type=' + loanTypeId : params + '&loan_type=' + loanTypeId;
    }

    if (loanStatus !== '') {
      params = (params === undefined || params === '') ? '?loan_status=' + loanStatus : params + '&loan_status=' + loanStatus;
    }

    if (loanSubStatus !== '') {
      params = (params === undefined || params === '') ? '?loan_sub_status=' + loanSubStatus : params + '&loan_sub_status=' + loanSubStatus;
    }

    if (paymentStatus !== '') {
      params = (params === undefined || params === '') ? '?payment_status=' + paymentStatus : params + '&payment_status=' + paymentStatus;
    }

    if (startDateFrom !== '') {
      params = (params === undefined || params === '') ? '?start_date_from=' + this.utilityService.convertDateToISOString(startDateFrom) : params + '&start_date_from=' + this.utilityService.convertDateToISOString(startDateFrom);
    }

    if (startDateTo !== '') {
      params = (params === undefined || params === '') ? '?start_date_to=' + this.utilityService.convertDateToISOString(startDateTo) : params + '&start_date_to=' + this.utilityService.convertDateToISOString(startDateTo);
    }

    if (maturityDateFrom !== '') {
      params = (params === undefined || params === '') ? '?maturity_date_from=' + this.utilityService.convertDateToISOString(maturityDateFrom) : params + '&maturity_date_from=' + this.utilityService.convertDateToISOString(maturityDateFrom);
    }

    if (maturityDateTo !== '') {
      params = (params === undefined || params === '') ? '?maturity_date_to=' + this.utilityService.convertDateToISOString(maturityDateTo) : params + '&maturity_date_to=' + this.utilityService.convertDateToISOString(maturityDateTo);
    }


    if (recordsPerPage !== 0) {
      params = (params === undefined || params === '') ? '?records_per_page=' + recordsPerPage : params + '&records_per_page=' + recordsPerPage;
    }

    return params;
  }

  getMemberBalance() {
    return this.memberBalance;
  }

  setMemberBalance(balance: number) {
    this.memberBalance = balance;
  }

  // Finance
  deposit(body: any): Observable<any> {
    const url = ENVIRONMENT.service.finance + '/Deposit/New';
    return this.baseService.post(url, body);
  }

  getBorrowerSummary(): Observable<any> {
    const url = ENVIRONMENT.service.financeV2 + `/borrowers/me/loans/summary`;
    // Possible fields for the summary
    // ie. ?fields=loan_payments_history,loan_installments_due,loan_installments
    const fields = ['loan_payments_history', 'loan_installments_due', 'loan_installments'];
    return this.baseService.get(url);
  }



  getBorrowerRepayments(args: IGetBorrowerRepaymentsParams): Observable<any> {
    let params = new HttpParams();
    if (args.fromDate) {
      params = params.append('from_date', args.fromDate.toISOString());
    }
    if (args.toDate) {
      params = params.append('to_date', args.toDate.toISOString());
    }
    if (args.loanId) {
      params = params.append('loan_id', args.loanId + '');
    }
    if (args.size) {
      params = params.append('size', args.size + '');
    }
    if (args.page) {
      params = params.append('page', args.page + '');
    }
    params = params.append('fields', 'loans,payments');
    const url = `${ENVIRONMENT.service.financeV2}/me/installments?${params.toString()}`;
    return this.baseService.get(url);
  }

  getBorrowerFirstIncompleteInstallments(loanIds?: Array<number>, getLoanData?: Boolean): Observable<any> {
    let params = new HttpParams();
    if (loanIds) {
      params = params.append('loan_ds', loanIds.join(','));
    }
    if (getLoanData) {
      params = params.append('fields', 'loans');
    }
    const url = `${ENVIRONMENT.service.financeV2}/me/installments/first_incomplete`;
    return this.baseService.get(url);
  }

  getBalance(): Observable<any> {
    const url = ENVIRONMENT.service.finance + '/Wallet/Balance/me';
    return this.baseService.get(url);
  }

  getDeposit(): Observable<any> {
    const url = ENVIRONMENT.service.finance + '/Deposit/Search/me';
    return this.baseService.get(url);
  }

  getMultipleLoans() {
    const url = ENVIRONMENT.service.finance + '/Funding/Loan/Available/Multiple?countrycode=' + CONFIGURATION.country_code;
    return this.baseService.get(url);
  }

  getInvestorPortfolioInstallments(paginate: boolean, page: number, recordsPerPage: number): Observable<any> {
    const url = ENVIRONMENT.service.finance + '/Installment/InstallmetPortFolio/me?paginate=' + paginate + '&page=' + page + '&records_per_page=' + recordsPerPage;
    return this.baseService.get(url);
  }

  getWithHoldingTaxCertificate(installmentId: string, investorId: string): Observable<any> {
    const token = this.authService.getBearerToken();
    const url = ENVIRONMENT.service.financeV2 + `/installments/wht_certificate?installment_id=${installmentId}&investor_id=${investorId}`;
    const headers = new HttpHeaders({
      Authorization: 'Bearer ' + token
    });
    return this.http.get(url, {headers: headers, responseType: 'blob' });
  }

  getInvestorPortfolioRepayments(paginate: boolean, loanTypeId: number, loanStatus: string, loanSubStatus: string,
    paymentStatus: string, startDateFrom: Date, startDateTo: Date, maturityDateFrom: Date,
    maturityDateTo: Date, page: number, recordsPerPage: number): Observable<any> {
    const url = ENVIRONMENT.service.finance + '/Payment/PaymentDetail/me' +
      this.generateParams(paginate, page, loanTypeId, loanStatus, loanSubStatus, paymentStatus, startDateFrom, startDateTo,
        maturityDateFrom, maturityDateTo, recordsPerPage);
    return this.baseService.get(url);
  }

  getInvestorPortolioRepaymentDetails(loanId: number): Observable<any> {
    const url = ENVIRONMENT.service.finance + '/Payment/PaymentDetail/Installments/' + loanId + '/me';
    return this.baseService.get(url);
  }

  getInvestorReturnsChart(period: number): Observable<any> {
    const url = ENVIRONMENT.service.finance + '/Investment/Portfolio/me?period=' + period;
    return this.baseService.get(url);
  }

  getInvestorSummary(): Observable<any> {
    const url = ENVIRONMENT.service.finance + '/Wallet/summary/me/investor?history=false';
    return this.baseService.get(url);
  }

  getInvestorOverview(): Observable<any> {
    const url = ENVIRONMENT.service.financeV2 + '/investors/me/portfolio';
    return this.baseService.get(url);
  }

  getStatements(): Observable<any> {
    const url = `${ENVIRONMENT.service.finance}/Wallet/month/me/list`;
    return this.baseService.get(url, [this.baseService.getCustomXHeaderLanguageKey()]);
  }

  getStatistics(countryCode: string): Observable<any> {
    const url = ENVIRONMENT.service.finance + ENVIRONMENT.service.public_identifier + '/Wallet/statistics/' + countryCode + '?truncateAmount=false';
    return this.baseService.get(url);
  }

  getRepaymentSummary(memberId: number): Observable<any> {
    const url = ENVIRONMENT.service.finance + '/Installment/upcoming_repayment/' + memberId
    return this.baseService.get(url);
  }

  getPortfolioCsvData(): Observable<any> {
    const url = ENVIRONMENT.service.financeV2 + '/investors/me/portfolio/detail';
    return this.baseService.get(url);
  }

  getWalletByMonth(page: number, total: number, search: string, orderBy: string,
    month: number, year: number): Observable<any> {
    const url = `${ENVIRONMENT.service.finance}/Wallet/month/me?
      page=${page}&size=${total}&search=${search}&orderby=${orderBy}&month=${month}&year=${year}`;
    return this.baseService.get(url, [this.baseService.getCustomXHeaderLanguageKey()]);
  }

  getWalletByPeriod(page: number, total: number, search: string, orderBy: string,
    fromDate: string, toDate: string): Observable<any> {
    const url = ENVIRONMENT.service.finance + '/Wallet/period/me?page=' + page + '&size=' + total + '&search=' + search + '&orderby=' + orderBy
      + '&fromDate=' + fromDate + '&toDate=' + toDate;
    return this.baseService.get(url, [this.baseService.getCustomXHeaderLanguageKey()]);
  }

  getPDFStatement(fromDate: string, toDate: string, monthly: boolean): Observable<Blob> {
    const defaultLanguage = this.languageService.getDefaultLanguage();
    const token = this.authService.getBearerToken();
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      ...(this.supportedLanguages.includes(defaultLanguage) ? { [this.baseService.getCustomXHeaderLanguageKey()]: defaultLanguage } : {}),
    });

    let params = new HttpParams();
    params = params.append('fromDate', fromDate);
    params = params.append('toDate', toDate);
    params = params.append('Monthly', monthly.toString());
    const url = `${ENVIRONMENT.service.finance}/Wallet/DownloadStatement?${params.toString()}`;
    return this.http.get(url, { headers: headers, responseType: 'blob' });
  }

  invest(body: any): Observable<any> {
    const url = ENVIRONMENT.service.crowdfunding + '/investment';
    return this.baseService.post(url, body);
  }

  triggerBalanceRetrieval(): void {
    this.balanceEventEmitter.emit(true);
  }

  withdraw(body: any): Observable<any> {
    const url = ENVIRONMENT.service.finance + '/Withdrawal/New';
    return this.baseService.post(url, body);
  }

  getTotalFunded(country: string) {
    const url = ENVIRONMENT.service.finance_public + '/Wallet/TotalFunded/' + country;
    return this.baseService.get(url);
  }

  optOutAutoInvestment(loanId: string) {
    const url = ENVIRONMENT.service.finance + '/Funding/AutoAllocation/optout?loan_id=' + loanId;
    return this.baseService.put(url, null).pipe(
      map(
        function (response: Response) {
          let result;
          try {
            if (response['_body'] === '') {
              result = {
                status: true
              };
            } else {
              result = response;
            }
          } catch (e) {
            throw new Error(response.toString());
          }
          return result;
        }
      ), catchError(this.baseService.errorHandler));
  }

  getTaxInvoices(memberId: number): Observable<any> {
    const url = `${ENVIRONMENT.service.financeV2}/members/me/tax_invoices?fields=date`;
    return this.baseService.get(url);
  }

  getPDFTaxInvoice(id: number, yearDate: number, monthDate: number, token: string): Observable<Blob> {
    const headers = new HttpHeaders({
      Authorization: 'Bearer ' + token
    });
    let params = new HttpParams();
    params = params.append('year', yearDate.toString());
    params = params.append('month', monthDate.toString());
    const url = `${ENVIRONMENT.service.financeV2}/members/me/tax_invoice?${params.toString()}`;
    return this.http.get(url, { headers: headers, responseType: 'blob' });
  }

  getActivities(page: number = 0, recordsPerPage: number = 0, type: string = ''): Observable<any> {
    let params = new HttpParams();
    if (page) params = params.append('page', String(page));
    if (recordsPerPage) params = params.append('size', String(recordsPerPage));
    if (type) params = params.append('types', String(type));
    const url = `${ENVIRONMENT.service.finance}/v2/investors/me/activities?${params.toString()}`;
    return this.baseService.get(url);
  }

  getIndonesiaStatistics(): Observable<any> {
    const url = ENVIRONMENT.service.v2 + '/statistics/ojk';
    return this.baseService.get(url);
  }

  getScDefaultRate(): Observable<any> {
    const url = ENVIRONMENT.service.v2 + '/statistics/sc';
    return this.baseService.get(url);
  }

  getLocalStatistics(countryCode: string): Observable<any> {
    const endPoint = ENVIRONMENT.service.v2;
    const url = endPoint + '/statistics/' + countryCode + '/default_definition';
    return this.baseService.get(url);
  }

  sendSATDisclaimer(body: any): Observable<any> {
    const url = ENVIRONMENT.service.finance + '/v2/investors/me/sat_disclaimer';
    return this.baseService.post(url, body);
  }

  getLoanExposure(): Observable<any> {
    const url = ENVIRONMENT.service.finance + '/v2/investors/me/loan_exposures';
    return this.baseService.get(url);
  }

  cancelInvestment(fundingId: number) {
    const url = ENVIRONMENT.service.financeV2 + '/investments/crowdfunding/' + fundingId;
    return this.baseService.delete(url);
  }

  generatePaymentCodes(paymentUniqueId: string) {
    const url = `${ENVIRONMENT.service.financeV2}/investments/payments/${paymentUniqueId}`;
    return this.baseService.get(url);
  }

  paymentReviewRequest(paymentUniqueId: string) {
    const url = `${ENVIRONMENT.service.financeV2}/investments/payments/${paymentUniqueId}/review`;
    return this.baseService.put(url, {});
  }

  getInvestmentPaymentStatus(loanId: string) {
    const url = `${ENVIRONMENT.service.financeV2}/investments/${loanId}/payments`;
    return this.baseService.get(url);
  }

  cancelPaymentRequest(paymentId: string) {
    const url = `${ENVIRONMENT.service.financeV2}/bank_transfers/deposit/${paymentId}/cancel`;
    return this.baseService.put(url, {});
  }

  getBulkLoans(memberId: number): Observable<any> {
    const url = ENVIRONMENT.service.financeV2 + '/fundings/loan/available/capital_channeling_partner?countrycode='+ CONFIGURATION.country_code + '&userid=' + memberId;
    return this.baseService.get(url);
  }

  postBulkInvestments(loanIds: any, member: any = null) {
    const url = ENVIRONMENT.service.financeV2 + '/investments/bulk_investments';
    return this.baseService.post(url, loanIds);
  }

  getCapitalPoaAgreements() {
    const url = `${ENVIRONMENT.service.financeV2}/investors/me/power_of_attorneys`;
    return this.baseService.get(url);
  }

  updateCapitalPoaAgreements(body: any) {
    const url = `${ENVIRONMENT.service.financeV2}/investments/capital_power_of_attorney`;
    return this.baseService.put(url, body);
  }

  getBulkInvestorPortfolioInstallments(queryParams: any) {
    let params = new HttpParams();
    if (queryParams.investment_date_from) params = params.append('investment_date_from', queryParams.investment_date_from);
    if (queryParams.investment_date_to) params = params.append('investment_date_to', queryParams.investment_date_to);
    if (queryParams.investment_date_from) params = params.append('paginate', queryParams.paginate + '');
    const url = `${ENVIRONMENT.service.financeV2}/investors/portfolio/capital_channeling_partner?${params.toString()}`;
    return this.baseService.get(url);
  }

  getBulkInvestorOverview() {
    const url = `${ENVIRONMENT.service.v2}/statistics/capital_channeling_partner/portfolio`;
    return this.baseService.get(url);
  }

  getBulkPortfolioCsvData(queryParams: any) {
    let params = new HttpParams();
    if (queryParams.investment_date_from) params = params.append('investment_date_from', queryParams.investment_date_from);
    if (queryParams.investment_date_to) params = params.append('investment_date_to', queryParams.investment_date_to);
    if (queryParams.paginate) params = params.append('paginate', queryParams.paginate);
    const url = `${ENVIRONMENT.service.financeV2}/investors/portfolio/capital_channeling_partner/detail?${params.toString()}`;
    return this.baseService.get(url);
  }

  getVerificationRequest() {
    const url = `${this.ekycEsignServiceUrl}/verification-request`;
    return this.baseService.get(url);
  }

  startPrivyDigitalSignatureSession(body) {
    const url = `${this.ekycEsignServiceUrl}/digital-signature-session`;
    return this.baseService.post(url, body);
  }

  getEsignInformation(reference: string, referenceType: string, digitalSignatureMethod: string): Observable<any> {
    const url = `${this.ekycEsignServiceUrl}/digital-signature-session/latest?reference=${reference}&referenceType=${referenceType}&digitalSignatureMethod=${digitalSignatureMethod}`;
    return this.baseService.get(url);
  }

  createPrivyVerificationRequest(body) {
    const url = `${this.ekycEsignServiceUrl}/verification-request`;
    return this.baseService.post(url, body);
  }
}
